import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, switchMap } from 'rxjs';
import { employeeActions } from 'src/app/shared/store/employees/actions';
import { IEmployeesDataState } from 'src/app/shared/store/employees/types/employees-data-state';
import { NavController } from '@ionic/angular/standalone';
import { WorkTimeSchemeService } from './services/worktimescheme.service';
import { workTimeSchemeActions } from './actions';
import { TranslateService } from '@ngx-translate/core';
import { toastActions } from 'src/app/features/toast/store/actions';
import { EAppRoutes } from '../../enums/app-routes.enum';
import { IWorkTimeSchemeResponseData } from './types/wortimescheme-response-data.interface';
import { getLocalizedDate } from '../../utils/util.functions';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';

@Injectable()
export class WorkTimeSchemeFeatureEffects {
  setCarryOverRecord$ = createEffect(
    (actions$ = inject(Actions), navCtrl = inject(NavController)) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.setCarryOverRecord),
        map((action) => {
          if (action.payload) {
            navCtrl.navigateForward([
              EAppRoutes.CARRYOVER_RECORD,
              action.payload.id,
            ]);
          }
        })
      ),
    { dispatch: false }
  );
  newWorkTimeScheme$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.addWorkTimeScheme),
        switchMap((action) => {
          return workTimeSchemeService
            .addNewWorkTimeScheme(action.payload)
            .pipe(
              map((response: IWorkTimeSchemeResponseData) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response.data,
                  })
                );
                return workTimeSchemeActions.addWorkTimeSchemeSuccess({
                  payload: {
                    message: response.message,
                    updated_end_date: response.updated_end_date,
                  },
                });
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.addWorkTimeSchemeFailure()
              )
            );
        })
      )
  );

  newWorkTimeSchemeSuccess$ = createEffect(
    (
      actions$ = inject(Actions),
      navCtrl = inject(NavController),
      translateService = inject(TranslateService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.addWorkTimeSchemeSuccess),
        map((action) => {
          navCtrl.back();
          const { message, id } = this.generateAlertMessage(
            action.payload.message,
            action.payload.updated_end_date,
            translateService,
            'new'
          );
          return toastActions.showToast({
            message: message,
          });
        })
      )
  );

  updateWorkTimeScheme$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.updateWorkTimeScheme),
        switchMap((action) => {
          return workTimeSchemeService
            .updateWorkTimeScheme(action.payload)
            .pipe(
              map((response: IWorkTimeSchemeResponseData) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response.data,
                  })
                );
                return workTimeSchemeActions.updateWorkTimeSchemeSuccess({
                  payload: {
                    message: response.message,
                    updated_end_date: response.updated_end_date,
                  },
                });
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.updateWorkTimeSchemeFailure()
              )
            );
        })
      )
  );

  updateWorkTimeSchemeSuccess$ = createEffect(
    (
      actions$ = inject(Actions),
      navCtrl = inject(NavController),
      translateService = inject(TranslateService),
      store = inject(Store)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.updateWorkTimeSchemeSuccess),
        map((action) => {
          navCtrl.back();
          const { message, id } = this.generateAlertMessage(
            action.payload.message,
            action.payload.updated_end_date,
            translateService,
            'update'
          );
          return toastActions.showToast({
            message: message,
          });
        })
      )
  );

  deleteWorkTimeScheme$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.deleteWorkTimeScheme),
        switchMap((action) => {
          return workTimeSchemeService
            .deleteWorkTimeScheme(action.payload)
            .pipe(
              map((response: IEmployeesDataState) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response,
                  })
                );
                return workTimeSchemeActions.deleteWorkTimeSchemeSuccess();
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.deleteWorkTimeSchemeFailure()
              )
            );
        })
      )
  );

  deleteWorkTimeSchemeSuccess$ = createEffect(
    (
      actions$ = inject(Actions),
      navCtrl = inject(NavController),
      translateService = inject(TranslateService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.deleteWorkTimeSchemeSuccess),
        map(() => {
          navCtrl.back();
          return toastActions.showToast({
            message: translateService.instant(
              'PAGES.STAFF.DELETE_WORKTIME_SCHEME_SUCCESS'
            ),
          });
        })
      )
  );

  deleteWorkTimeSchemeCard$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.deleteWorkTimeSchemeCard),
        switchMap((action) => {
          return workTimeSchemeService
            .deleteWorkTimeScheme(action.payload)
            .pipe(
              map((response: IEmployeesDataState) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response,
                  })
                );
                return workTimeSchemeActions.deleteWorkTimeSchemeCardSuccess();
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.deleteWorkTimeSchemeFailure()
              )
            );
        })
      )
  );

  deleteWorkTimeSchemeCardSuccess$ = createEffect(
    (actions$ = inject(Actions), translateService = inject(TranslateService)) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.deleteWorkTimeSchemeCardSuccess),
        map(() => {
          return toastActions.showToast({
            message: translateService.instant(
              'PAGES.STAFF.DELETE_WORKTIME_SCHEME_SUCCESS'
            ),
          });
        })
      )
  );

  newCarryOverRecord$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.addCarryOverRecord),
        switchMap((action) => {
          return workTimeSchemeService
            .addNewCarryOverRecord(action.payload)
            .pipe(
              map((response: IEmployeesDataState) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response,
                  })
                );
                return workTimeSchemeActions.addCarryOverRecordSuccess();
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.addCarryOverRecordFailure()
              )
            );
        })
      )
  );

  newCarryOverRecordSuccess$ = createEffect(
    (
      actions$ = inject(Actions),
      navCtrl = inject(NavController),
      translateService = inject(TranslateService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.addCarryOverRecordSuccess),
        map(() => {
          navCtrl.back();
          return toastActions.showToast({
            message: translateService.instant(
              'PAGES.STAFF.NEW_TIME_CREDIT_SUCCESS'
            ),
          });
        })
      )
  );

  updateCarryOverRecord$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.updateCarryOverRecord),
        switchMap((action) => {
          return workTimeSchemeService
            .updateCarryOverRecord(action.payload)
            .pipe(
              map((response: IEmployeesDataState) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response,
                  })
                );
                return workTimeSchemeActions.updateCarryOverRecordSuccess();
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.updateCarryOverRecordFailure()
              )
            );
        })
      )
  );

  updateCarryOverRecordSuccess$ = createEffect(
    (
      actions$ = inject(Actions),
      navCtrl = inject(NavController),
      translateService = inject(TranslateService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.updateCarryOverRecordSuccess),
        map(() => {
          navCtrl.back();
          return toastActions.showToast({
            message: translateService.instant(
              'PAGES.STAFF.EDIT_TIME_CREDIT_SUCCESS'
            ),
          });
        })
      )
  );

  deleteCarryOverRecord$ = createEffect(
    (
      actions$ = inject(Actions),
      workTimeSchemeService = inject(WorkTimeSchemeService),
      store = inject(Store),
      errorHandlerService = inject(ErrorHandlerService)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.deleteCarryOverRecord),
        switchMap((action) => {
          return workTimeSchemeService
            .deleteCarryOverRecord(action.payload)
            .pipe(
              map((response: IEmployeesDataState) => {
                store.dispatch(
                  employeeActions.successFetchingEmployees({
                    payload: response,
                  })
                );
                return workTimeSchemeActions.deleteCarryOverRecordSuccess();
              }),
              errorHandlerService.handleEffectError(
                workTimeSchemeActions.deleteCarryOverRecordFailure()
              )
            );
        })
      )
  );

  deleteCarryOverRecordSuccess$ = createEffect(
    (
      actions$ = inject(Actions),
      translateService = inject(TranslateService),
      navCtrl = inject(NavController)
    ) =>
      actions$.pipe(
        ofType(workTimeSchemeActions.deleteCarryOverRecordSuccess),
        map(() => {
          navCtrl.back();
          return toastActions.showToast({
            message: translateService.instant(
              'PAGES.STAFF.DELETE_TIME_CREDIT_SUCCESS'
            ),
          });
        })
      )
  );

  workTimeSchemeFailedActions$ = createEffect(
    (actions$ = inject(Actions), navCtrl = inject(NavController)) =>
      actions$.pipe(
        ofType(
          workTimeSchemeActions.addWorkTimeSchemeFailure,
          workTimeSchemeActions.updateWorkTimeSchemeFailure,
          workTimeSchemeActions.deleteWorkTimeSchemeFailure,
          workTimeSchemeActions.addCarryOverRecordFailure,
          workTimeSchemeActions.updateCarryOverRecordFailure,
          workTimeSchemeActions.deleteCarryOverRecordFailure
        ),
        map(() => {
          navCtrl.back();
        })
      ),
    { dispatch: false }
  );

  private generateAlertMessage(
    response_message: string,
    response_date: string,
    translateService: TranslateService,
    type: 'new' | 'update' = 'new'
  ): { message: string; id: string } {
    console.log(response_message, response_date);
    let message = '';
    let id = '';
    let date = response_date ? getLocalizedDate(new Date(response_date)) : null;

    switch (type) {
      case 'new':
        message = translateService.instant(
          'PAGES.STAFF.NEW_WORKTIME_SCHEME_SUCCESS',
          { date: date }
        );
        id = 'PAGES.STAFF.NEW_WORKTIME_SCHEME_SUCCESS';
        break;
      case 'update':
        message = translateService.instant(
          'PAGES.STAFF.EDIT_WORKTIME_SCHEME_SUCCESS',
          { date: date }
        );
        id = 'PAGES.STAFF.EDIT_WORKTIME_SCHEME_SUCCESS';
        break;
    }

    switch (response_message) {
      case 'New end_date set automatically.':
      case 'New and previous end_date set automatically.':
        message =
          message +
          '. <br> ' +
          translateService.instant('PAGES.STAFF.END_DATE_CHANGED', {
            endDate: date,
          }) +
          '.';
        id = 'PAGES.STAFF.END_DATE_CHANGED';
        break;
      case 'Previous end_date set automatically.':
        message =
          message +
          '. <br> ' +
          translateService.instant('PAGES.STAFF.END_DATE_CHANGED_PREVIOUS', {
            endDate: date,
          }) +
          '.';
        id = 'PAGES.STAFF.END_DATE_CHANGED_PREVIOUS';
        break;
    }

    return { message, id };
  }
}
